import { Link } from 'react-router'
import { Badge } from '#app/components/ui/badge.tsx'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from '#app/components/ui/card.tsx'
import { Icon, type IconName } from '#app/components/ui/icon.tsx'
import {
	EQUIPMENT_TYPES,
	PROJECT_GUIDES,
} from '../equipment-cost-estimator.data.ts'

const GUIDE_BORDER_COLOR: Record<string, string> = {
	'tree-stump-removal': 'border-t-green-500',
	'patio-walkway-prep': 'border-t-slate-500',
	'hardwood-floor-refinish': 'border-t-violet-500',
	'spring-yard-cleanup': 'border-t-emerald-500',
}

export function EquipmentProjectGuides() {
	return (
		<section>
			<div className="flex items-center gap-3">
				<div className="bg-primary/10 flex size-10 items-center justify-center rounded-full">
					<Icon name="clipboard-list" size="md" className="text-primary" />
				</div>
				<div>
					<Badge variant="secondary" className="mb-1">
						Project Guides
					</Badge>
					<h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
						DFW Project Equipment Guides
					</h2>
				</div>
			</div>
			<p className="text-muted-foreground mt-2">
				Not sure what equipment you need? These guides walk you through common
				DFW projects with recommended rentals and pro tips.
			</p>

			<div className="mt-8 grid gap-6 sm:grid-cols-2">
				{PROJECT_GUIDES.map((guide) => (
					<Card
						key={guide.slug}
						className={`border-t-4 transition-shadow hover:shadow-md ${GUIDE_BORDER_COLOR[guide.slug] ?? 'border-t-primary'}`}
					>
						<CardHeader>
							<div className="flex items-center gap-3">
								<div className="bg-primary/10 flex size-8 items-center justify-center rounded-full">
									<Icon
										name={guide.icon as IconName}
										size="sm"
										className="text-primary"
									/>
								</div>
								<CardTitle>{guide.title}</CardTitle>
							</div>
							<p className="text-muted-foreground text-sm">
								{guide.description}
							</p>
						</CardHeader>
						<CardContent className="space-y-4">
							{/* Equipment list */}
							<div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950/30">
								<p className="mb-2 text-xs font-semibold uppercase tracking-wider text-blue-700 dark:text-blue-300">
									Equipment Needed
								</p>
								<ul className="space-y-1.5">
									{guide.equipmentSlugs.map((slug) => {
										const eq = EQUIPMENT_TYPES.find((e) => e.slug === slug)
										if (!eq) return null
										return (
											<li key={slug} className="flex items-center gap-2">
												<Icon
													name="drill"
													size="sm"
													className="shrink-0 text-blue-500"
												/>
												<a
													href={`#${slug}`}
													className="text-sm font-medium text-blue-700 hover:underline dark:text-blue-300"
												>
													{eq.name}
												</a>
											</li>
										)
									})}
								</ul>
							</div>

							{/* Tips */}
							<div>
								<p className="text-muted-foreground mb-2 text-xs font-semibold uppercase tracking-wider">
									Pro Tips
								</p>
								<ul className="space-y-2">
									{guide.tips.map((tip, idx) => (
										<li
											key={idx}
											className="text-muted-foreground flex items-start gap-2 text-sm"
										>
											<span className="mt-0.5 shrink-0">
												<Icon
													name="check"
													size="sm"
													className="text-green-500"
												/>
											</span>
											{tip}
										</li>
									))}
								</ul>
							</div>
						</CardContent>
					</Card>
				))}
			</div>

			{/* Cross-link to dumpster calculator */}
			<div className="mt-8 rounded-lg border border-amber-200 bg-amber-50 p-5 dark:border-amber-800 dark:bg-amber-950/30">
				<div className="flex items-start gap-3">
					<div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/50">
						<Icon
							name="truck"
							size="sm"
							className="text-amber-600 dark:text-amber-400"
						/>
					</div>
					<div>
						<h3 className="text-sm font-semibold text-amber-800 dark:text-amber-300">
							Need a Dumpster for Debris?
						</h3>
						<p className="text-muted-foreground mt-1 text-sm">
							Many of these projects generate debris that needs hauling away.{' '}
							<Link
								to="/tools/dumpster-size-calculator"
								className="text-primary font-medium hover:underline"
							>
								Use our Dumpster Size & Cost Calculator
							</Link>{' '}
							to find the right size and compare DFW dumpster rental prices.
						</p>
					</div>
				</div>
			</div>
		</section>
	)
}
